import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { useLocation } from "@reach/router"

function Seo({
  description,
  lang,
  meta,
  keywords,
  title,
  image,
  article,
  author,
  publishedTime,
  modifiedTime,
  url,
}) {
  const { pathname } = useLocation()
  const data = useStaticQuery(detailsQuery)

  const siteUrl = data.site.siteMetadata.siteUrl || ""
  const metaDescription = description || data.site.siteMetadata.description
  const metaImage = image
    ? image.startsWith("http")
      ? image
      : `${siteUrl}${image}`
    : `${siteUrl}/images/upfeed-logo.png`
  const path = url || pathname || "/"
  const metaUrl = `${siteUrl}${path}`
  const ogType = article ? "article" : "website"

  const metaTags = [
    {
      name: `description`,
      content: metaDescription,
    },
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: ogType,
    },
    {
      property: `og:url`,
      content: metaUrl,
    },
    {
      property: `og:image`,
      content: metaImage,
    },
    {
      property: `og:site_name`,
      content: data.site.siteMetadata.title,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:creator`,
      content: data.site.siteMetadata.author,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: `twitter:image`,
      content: metaImage,
    },
  ]

  // Add article-specific meta tags
  if (article) {
    if (publishedTime) {
      metaTags.push({
        property: `article:published_time`,
        content: publishedTime,
      })
    }
    if (modifiedTime) {
      metaTags.push({
        property: `article:modified_time`,
        content: modifiedTime,
      })
    }
    if (author) {
      metaTags.push({
        property: `article:author`,
        content: author,
      })
    }
  }

  // Add keywords
  if (keywords && keywords.length > 0) {
    metaTags.push({
      name: `keywords`,
      content: keywords.join(`, `),
    })
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | Upfeed`}
      link={[
        {
          rel: `canonical`,
          href: metaUrl,
        },
      ]}
      meta={metaTags.concat(meta)}
    />
  )
}

Seo.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
}

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  article: PropTypes.bool,
  author: PropTypes.string,
  publishedTime: PropTypes.string,
  modifiedTime: PropTypes.string,
  url: PropTypes.string,
}

const detailsQuery = graphql`
  query DefaultSEOQuery {
    site {
      siteMetadata {
        title
        description
        author
        siteUrl
      }
    }
  }
`

export default Seo
